import {
  useApiBenefitConfigurationV10ChangesApproveCreate,
  useApiBenefitConfigurationV10ChangesRejectCreate,
  useApiBenefitConfigurationV10ChangesRetrieve,
} from '@ben/api/gen';
import BackHeader from '@ben/orion/back-header';
import Banner from '@ben/orion/banner';
import Button from '@ben/orion/button';
import { InformationCard } from '@ben/orion/information-card';
import Loader from '@ben/orion/loader';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { Anchor, Grid, Group, Stack, Text } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { domainLabel } from './domains';
import { FieldDiffTable, toFieldDiffRows } from './field-diff-table';
import { PayloadPreview } from './payload-preview';
import { apiErrorMessage, invalidateWorkspaceCaches } from './queries';
import { decidableReasonText, Metadata, Muted, Page, Pre } from './shared';
import { StatusTag } from './status';
import type { WorkspaceStatus } from './status';

const ChangeDetailScreen = () => {
  const { changeId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);

  const { data, isLoading } = useApiBenefitConfigurationV10ChangesRetrieve(
    changeId,
    { query: { enabled: Boolean(changeId) } }
  );

  const onDecisionSuccess = useCallback(
    (verb: string) => {
      setMessage(`Decision (${verb}) sent. The status will update shortly.`);
      invalidateWorkspaceCaches(queryClient);
    },
    [queryClient]
  );
  const onDecisionError = useCallback((error: unknown) => {
    setMessage(apiErrorMessage(error, 'The decision could not be submitted.'));
  }, []);

  const approve = useApiBenefitConfigurationV10ChangesApproveCreate({
    mutation: {
      onSuccess: () => onDecisionSuccess('approve'),
      onError: onDecisionError,
    },
  });
  const reject = useApiBenefitConfigurationV10ChangesRejectCreate({
    mutation: {
      onSuccess: () => onDecisionSuccess('reject'),
      onError: onDecisionError,
    },
  });

  const isDeciding = approve.isLoading || reject.isLoading;
  const hasDecided = approve.isSuccess || reject.isSuccess;

  const onApprove = useCallback(
    () => approve.mutate({ changeId }),
    [approve, changeId]
  );
  const onReject = useCallback(
    () => reject.mutate({ changeId }),
    [reject, changeId]
  );
  const onBack = useCallback(() => {
    navigate(
      data?.requirementGroup
        ? `/requirements/groups/${data.requirementGroup.id}`
        : '/requirements'
    );
  }, [data?.requirementGroup, navigate]);

  if (isLoading) return <Loader />;
  if (!data) return <Muted>Configuration change not found.</Muted>;

  const diffRows = toFieldDiffRows(data.fieldChanges);

  return (
    <Page $noTopPad>
      <BackHeader
        noMargin
        title={
          data.requirementGroup
            ? `Back to ${data.requirementGroup.name}`
            : 'Back to requirements'
        }
        onBack={onBack}
      />

      <Typography variant={TypographyVariant.h1}>
        {domainLabel(data.domain)} change
      </Typography>

      {/* Two-column block: main content left (~70%), sidebar right (~30%). */}
      <Grid gap="xl">
        <Grid.Col span={{ base: 12, lg: 8 }}>
          <Stack gap="xl">
            <Text>{data.summary}</Text>

            <Group>
              <Button
                onClick={onApprove}
                disabled={!data.decidable || isDeciding || hasDecided}
                loading={approve.isLoading}
              >
                Approve
              </Button>
              <Button
                onClick={onReject}
                disabled={!data.decidable || isDeciding || hasDecided}
                loading={reject.isLoading}
                variant="outline"
              >
                Reject
              </Button>
            </Group>

            {message ? (
              <Banner variant="information" message={message} />
            ) : null}

            {data.decidable ? null : (
              <Banner
                variant="warning"
                message={decidableReasonText(data.decidableReason)}
              />
            )}

            {data.revisionStatus.isStale ? (
              <Banner
                variant="critical"
                title="No longer current"
                message="The benefit has changed since this was generated. Applying it would overwrite that."
              />
            ) : null}

            <section>
              <Typography variant={TypographyVariant.h2}>
                What this changes
              </Typography>
              {diffRows.length === 0 ? (
                <Muted>
                  Nothing to compare — this proposal has no revisioned fields.
                </Muted>
              ) : (
                <FieldDiffTable
                  rows={diffRows}
                  truncated={data.fieldChangesTruncated}
                />
              )}
            </section>

            <section>
              <Typography variant={TypographyVariant.h2}>Why</Typography>
              <Muted>{data.changeReason}</Muted>
            </section>

            <section>
              <Typography variant={TypographyVariant.h2}>
                Full proposed payload
              </Typography>
              <Pre>{JSON.stringify(data.payload, null, 2)}</Pre>
            </section>
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 4 }}>
          <Stack gap="md">
            <InformationCard
              title="Details"
              titleVariant={TypographyVariant.h3}
              gap={2}
            >
              <Metadata>
                <dt>Status</dt>
                <dd>
                  <StatusTag status={data.status as WorkspaceStatus} />
                </dd>
                <dt>Benefit</dt>
                <dd>
                  {data.benefit ? (
                    <Link to={`/benefits/${data.benefit.id}`}>
                      {data.benefit.name}
                    </Link>
                  ) : (
                    'Not created yet'
                  )}
                </dd>
                <dt>Run</dt>
                <dd>{data.benefitConfigurationRun ?? '—'}</dd>
                <dt>Proposed by</dt>
                <dd>{data.source}</dd>
                <dt>Still current</dt>
                <dd>
                  {data.revisionStatus.isStale === null
                    ? 'Unknown'
                    : data.revisionStatus.isStale
                      ? 'No — the benefit has moved on'
                      : 'Yes'}
                </dd>
              </Metadata>
            </InformationCard>

            <InformationCard
              title="Requirements behind it"
              titleVariant={TypographyVariant.h3}
              gap={2}
            >
              {data.requirements.length === 0 ? (
                <Muted>None were linked when this was generated.</Muted>
              ) : (
                <Stack gap="sm">
                  {data.requirements.map((req) => (
                    <div key={req.id}>
                      <Anchor
                        component={Link}
                        to={`/requirements/groups/${data.requirementGroup?.id ?? req.groupId}?requirement=${req.id}`}
                        size="sm"
                      >
                        {req.name}
                      </Anchor>
                      <Muted>
                        {domainLabel(req.groupDomain)}
                        {req.isCurrent
                          ? ''
                          : ' · changed since this was generated'}
                      </Muted>
                    </div>
                  ))}
                </Stack>
              )}
            </InformationCard>
          </Stack>
        </Grid.Col>
      </Grid>

      {/* Full-width — Columbia's benefit sections are designed for a full page
          width and overflow a constrained column. */}
      <PreviewSection>
        <Typography variant={TypographyVariant.h2}>
          How it would look
        </Typography>
        <Muted>
          The same sections you configure a benefit in, showing the proposed
          values.
        </Muted>
        <PayloadPreview
          domain={data.domain}
          payload={data.payload as Record<string, unknown>}
          benefitId={data.benefit?.id}
        />
      </PreviewSection>
    </Page>
  );
};

export default ChangeDetailScreen;

const PreviewSection = styled.section`
  ${({ theme }) => css`
    display: grid;
    gap: ${theme.spacing(2)};
  `}
`;
